import { useMemo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Fragment, useEffect, useLayoutEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useParams } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';

import { ReactComponent as ArrowLeft } from 'assets/icons/circle-arrow-left.svg';
import PageLayout from 'components/layout/PageLayout';
import Flex from 'components/ui/Flex';
import Typography from 'components/ui/Typography';
import { breakPoint, palette } from 'shared/styles';
import { PositionKey } from 'type';
import { useScroll } from 'store/ScrollContext';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    kakao: any;
  }
}

const positionMap: {
  [key in PositionKey]: {
    title: string;
    role?: string;
    team: string;
    type: '정규직' | '비 정규직';
    mainTasks: string[];
    qualifications: string[];
    technicalSkills?: string[]; // 기술 역량
    skills?: string[];
    preferentials?: string[]; // 기존 우대사항
    newPreferentials?: { title: string; contents: string[] }[];
    isDeveloper?: boolean;
    year?: string;
    positionIntroduction?: string[];
  };
} = {
  'it-platform-sales': {
    title: '화장품 인허가 IT플랫폼 영업',
    team: '자동화솔루션팀',
    type: '정규직',
    mainTasks: [
      '• 화장품 국내/해외 인증 서비스 영업',
      '• 화장품 국내/해외 인증 자동화 프로그램 사용 및 교육',
      '• 인증 대행 및 수행 커뮤니케이션',
      '• 데이터 기반의 화장품 컨설팅',
    ],
    qualifications: [
      '• 학력 : 대졸 이상(2,3년)',
      '• 경력 : 3년 ~ 10년(팀원급)',
    ],
    preferentials: [
      '• 화장품 브랜드사 제품교육 강사 경력자',
      '• 화장품 브랜드사 CS 담당 경력자',
      '• 화장품 브랜드사 및 제조사, 부자재사 영업 경력자',
      '• IT플랫폼 고객유치 영업 경력',
    ],
    year: '3년 ~ 10년(팀원급)',
  },
  'ui-ux-designer': {
    title: 'UI/UX 디자이너',
    team: '서비스개발팀',
    type: '정규직',
    mainTasks: [
      '• certicos 신규 웹 서비스 UI/UX 디자인',
      '• certicos 화장품 인증 B2B 서비스 UI/UX 디자인 고도화',
    ],
    qualifications: [
      '• 상용화된 웹 / 앱 서비스의 UI/UX 디자인 경험이 있으신 분',
      '• Sketch, Zeplin 등의 디자인관련 툴 사용가능하신 분',
      '• 협업을 중요시하며 유연한 커뮤니케이션 능력을 지니신 분',
      '• 사용자 관점에서 능동적으로 솔루션을 제시할 수 있는 분',
    ],
    preferentials: [
      '• 적극적이고 주도적으로 서비스를 기획한 경험이 있으신 분',
      '• 구글/애플 디자인 가이드 라인에 대한 깊은 이해도가 있으신 분',
      '• 변화하는 상황 속에서 유연하고 능동적으로 문제를 해결할 수 있는 분',
      '• 서비스의 초기 UI/UX설계부터 출시, 운영 경험이 있으신 분',
    ],
  },
  'e-commerce-planner': {
    title: '서비스 기획자',
    team: '서비스개발본부',
    type: '정규직',
    mainTasks: [
      '• 기획디자인팀(팀장, 기획자, UI/UX디자이너로 구성) 소속되어 스프린트 단위로 담당 웹/앱 솔루션 고도화.',
      '• 화장품 인증 B2B 서비스 Certicos 고도화 및 운영 기획.',
      '• AI 솔루션 고도화 및 운영 기획.',
      '• 사내 ERP 신규 구축 및 고도화 기획.',
    ],
    qualifications: [
      '• 3년~10년 사이의 IT 서비스 기획 경험을 보유하신 분',
      '• 프로젝트 일정 및 계획을 수립하고 진행 과정을 주도적으로 관리해보신 분',
      '• 제품 및 서비스를 개선해 실제 성과를 만들어본 경험이 있으신 분',
      '• 다양한 이해관계자와 원활하게 소통하고 협업하며 프로젝트를 조율해본 경험이 있으신 분',
    ],
    skills: ['• Jira Software, Notion, Figma, Slack'],
    preferentials: [
      '• ERP 시스템 구축 또는 디지털 전환 프로젝트 경험',
      '• 상용화된 E-커머스(플랫폼) 기획 경험',
      '• 생성형 AI, LLM, 이미지 생성 AI(Diffusion 등)에 대한 기본적인 이해 및 활용 능력',
      '• 복잡한 도메인을 탐구하고 구조화해내는 과정을 즐기시는 분',
    ],
    year: '3~10년',
  },
  'frontend-developer': {
    title: '프론트엔드 개발자',
    team: '서비스개발본부',
    type: '정규직',
    mainTasks: [
      "• '화장품 인증 B2B 서비스' Certicos 프론트엔드 고도화 및 운영",
      "• '화장품 커뮤니티' Cosbridge 프론트엔드 고도화 및 운영",
      "• '임상 서비스' Cosmetest 프론트엔드 고도화 및 운영",
      "• 'AI 기반 개발 도구 활용'을 통한 개발 생산성 향상",
      "• '최신 프론트엔드 기술 트렌드' 적용 및 아키텍처 개선",
    ],
    qualifications: [
      "• 'React 생태계'를 깊이 이해하고, 현대적인 개발 패턴을 적용하는 분",
      "• 'TypeScript'를 활용한 타입 안전한 개발에 능숙한 분",
      "• 'AI 도구와의 협업'에 적극적이고, 새로운 기술 학습에 열정적인 분",
      "• '논리적 문제 해결' 능력과 '시스템적 사고'를 바탕으로 한 개발 역량",
      "• '책임감과 주도성'을 가지고 팀과 함께 성장하는 분",
    ],
    technicalSkills: [
      "• 'HTML5', 'CSS3', 'JavaScript/TypeScript' 등 프론트엔드 핵심 기술 숙련",
      "• 'React v17~19' 다양한 버전 활용 경험",
      "• '상태 관리(Zustand, Recoil, Redux, MobX)' 및 '서버 상태 관리(React Query, SWR)'",
      "• '모던 빌드 도구' 및 '번들링 최적화' 경험",
    ],
    skills: [
      '• React.js, Next.js, Redux',
      '• Javascript, TypeScript, AWS',
      '• HTML5, CSS3',
      '• JIRA, Slack, Notion',
    ],
    newPreferentials: [
      {
        title: '[AI 협업 환경]',
        contents: [
          "• 'Cursor.ai', 'GitHub Copilot', 'Claude' 등 AI 코딩 도구 활용 경험",
          "• 'AI 페어 프로그래밍' 및 'AI 기반 코드 리뷰' 경험",
          "• '프롬프트 엔지니어링'을 통한 효율적인 AI 협업 능력",
        ],
      },
      {
        title: '[최신 기술 트렌드]',
        contents: [
          "• 'Web Components' 및 'Micro Frontend' 아키텍처 이해",
          "• 'WebAssembly(WASM)' 및 'Web Workers' 활용 경험",
          "• 'Progressive Web App(PWA)' 개발 및 최적화 경험",
        ],
      },
      {
        title: '[성능 및 품질]',
        contents: [
          "• 'Core Web Vitals' 최적화 및 '웹 성능 모니터링' 경험",
          "• '웹 접근성 (WCAG 2.1)' 및 '웹 표준' 준수 경험",
          "• '크로스 브라우징' 및 '반응형 웹' 개발 숙련도",
          "• '테스트 주도 개발 (TDD)' 및 '컴포넌트 테스팅' 경험",
        ],
      },
      {
        title: '[개발 문화]',
        contents: [
          "• '애자일/스크럼' 방법론을 통한 협업 경험",
          "• '코드 리뷰' 문화에 적극 참여하는 분",
          "• '기술 블로그 작성' 또는 '컨퍼런스 발표' 경험",
          "• '오픈소스 기여' 또는 '개인 프로젝트' 운영 경험",
        ],
      },
    ],
    isDeveloper: true,
    year: '3년~8년',
  },
  'backend-developer': {
    title: '백엔드 개발자',
    team: '서비스개발팀',
    type: '정규직',
    positionIntroduction: [
      '• 화장품 TIC (Test, Inspection, Certification) 산업에서 복잡한 도메인을 단순화하고 변화에 빠르게 대응할 수 있도록 데이터 설계 및 관리합니다',
      '• Al 를 이용하여 사용자들의 업무의 효율을 올릴 수 있도록 기능 개발을 합니다',
      '• 복잡하고 많은 데이터들의 규칙을 분석하여, 데이터를 사용자에게 제공할 수 있는 알고리즘을 개발합니다',
      '• 다양하고 복잡한 권한에 따른 서비스 관련 기능을 개발, 개선 합니다',
      '• 전산화 되지 않는 데이터를 자동화하여 효율적으로 데이터를 모으고 관리합니다.',
      '• 화장품 인증 B2B 서비스인 Certicos, 임상 서비스인 Cosmetest 서비스 등 안정적으로 인프라 운영합니다.',
    ],
    mainTasks: [
      '• 화장품 인증 B2B 서비스(CERTICOS) 고도화 백엔드 개발 및 운영',
      '• 화장품 커뮤니티(COSBRIDGE) 고도화 백엔드 개발 및 운영',
      '• 임상 서비스(COSMETEST) 고도화 백엔드 개발 및 운영',
      '• AI 부서와 협업하여 AI 기능 관련 기능 알고리즘과 관련 API 설계, 개선 및 구현',
      '• AWS 등 클라우드 환경에서 인프라 운영 및 자동화 배포',
    ],
    qualifications: [
      '• 상용 웹/앱 서비스의 백엔드 개발 경험자',
      '• Java, Spring Boot에 능숙한 분',
      '• MySQL, MariaDB 사용 경험 보유',
      '• 시스템 구조와 코드 설계에 대해 깊이 고민하는 분',
      '• 유연한 커뮤니케이션과 팀워크에 강점 있는 분',
    ],
    skills: [
      '• Java, Spring Boot, Kotlin',
      '• MySQL, MongoDB, NoSQL',
      '• AWS, Redis',
      '• JIRA, Slack',
    ],
    preferentials: [
      '• AWS 기반 서비스 운영 경험',
      '• Redis, MongoDB 등 NoSQL 활용 경험',
      '• DB 모델링 및 쿼리 튜닝 경험',
      '• 초기 설계부터 운영까지 전 주기 경험자',
      '• 도메인 복잡도가 높은 서비스 경험자',
      '• 문제 해결에 능동적이며, 코드와 서비스의 결과를 검증해본 분',
    ],
    isDeveloper: true,
    year: '3년 ~ 8년',
  },
  'ai-engineer': {
    title: 'AI 엔지니어',
    team: '서비스개발팀',
    type: '정규직',
    mainTasks: [
      '• 글로벌 화장품 규제 데이터를 통한 분류/예측 등의 AI 알고리즘 개발',
      '• 인증관련 글로벌 빅데이터 수집/가공/처리 수행',
    ],
    qualifications: [
      '• AI관련 상용화된 웹 / 앱 서비스 개발 경험이 있으신 분',
      '• 딥러닝 프레임워크 활용에 능숙하신 분(Pytorch, Tensorflow 등)',
      '• 업무 분야와 관련된 논문을 분석하고 구현, 검증하는 것에 관심 많으신 분',
      '• 새로운 기술에 적극적이고 능동적인 분',
      '• 협업에 적극적이고, 커뮤니케이션을 유연하게 잘 하시는 분',
    ],
    preferentials: [
      '• 인공지능 관련 학과 석사 이상 우대',
      '• 머신러닝/딥러닝 분야 경력 3년 이상 우대',
      '• 다국어 자연어 처리 가능하신 분 우대',
    ],
    isDeveloper: true,
  },
  'cosmetic-ra': {
    title: '국내 화장품 해외 진출 지원(RA) 담당자 상시 채용',
    role: '국내 화장품 해외 진출 지원(RA) 담당자',
    team: '자동화솔루션본부',
    type: '정규직',
    mainTasks: [
      '• 각 국가별 수출 서류 준비',
      '• 브랜드 국내외 인허가 담당(CPNP, MoCRA 등)',
      '• 브랜드 제품 관련 영문 문구/문안 검수',
    ],
    qualifications: [
      '• 경력 : 1년 이상',
      '• 학력 : 초대졸 이상',
      '• 화장품 관련 기초 지식이 있으신 분',
      '• 원활한 커뮤니케이션 능력 및 협업성을 갖추신 분',
    ],
    preferentials: ['• 영어 문서 확인에 어려움이 없는 분'],
    year: '1년~10년',
  },
  'cosmetic-ra-junior': {
    title: '화장품 글로벌 수출 인허가 담당자(신입)',
    role: '화장품 글로벌 수출 인허가 담당자',
    team: '자동화솔루션본부',
    type: '정규직',
    mainTasks: [
      '• 미국, 유럽, 중국 등 주요 글로벌 국가의 화장품 인허가 실무 및 규정 대응 업무 (CPNP, MoCRA, NMPA, CDSCO, Vegan 등)',
      '• 단상자, 라벨 등의 국가별 규제 적합성 검토 및 문구 정리',
      '• 금지 성분, 금지 표현, 사용상 주의사항 등 국가별 법규/규정의 이해 및 적용',
      '• CPNP, SCPN 등 포털 등록용 서류 준비 및 관리(PIF, SDS 등)',
      '• 수출용 문서 번역본 검토 및 고객사 커뮤니케이션 지원',
    ],
    qualifications: [
      '• 경력 : 신입(글로벌 환경에서 커리어를 시작하고 싶은 분 환영)',
      '• 학력 : 초대졸 이상',
      '• 화장품 산업 또는 글로벌 규제/수출 환경에 대한 관심이 있는 분',
      '• 문서 검토와 정리에 익숙하고, 세부적인 내용에 주의를 기울이는 분',
      '• 밝고 책임감 있는 태도, 팀과의 협업을 소중이 여기는 분',
    ],
    preferentials: [
      '• 화학 계열 전공자',
      '• 생명 계열 전공자',
      '• 화장품학과 전공자',
      '• 영어 / 스페인어 / 러시아어 / 일본어 / 중국어 등 외국어 역량 보유자',
    ],
    year: '신입',
  },
  consultant: {
    title: '컨설턴트',
    team: '컨설팅본부',
    type: '정규직',
    mainTasks: [
      '• 화장품 해외수출 인허가, 인체적용시험, 시험분석, 품질관리 등에 대한 컨설팅 및 영업',
      '• 화장품 원료사/제조사/브랜드사 대상 해외 인증 전산화 IT 서비스 영업',
      '• 화장품 원료사/제조사/브랜드사 대상의 커뮤니티 활성화',
    ],
    qualifications: [
      '• 경력 : 무관(신입 지원 가능)',
      '• 영업적 역량과 적극적인 성향을 지니신 분',
      '• 고객의 입장에서 생각하고 니즈를 파악할 수 있는 분',
      '• 적극적인 태도와 긍정적인 마인드를 보유하신 분',
    ],
    preferentials: [
      '• 화장품 산업에 대한 이해가 높으신 분',
      '• 화장품 제조사 영업 경험',
    ],
    year: '무관',
  },
  'data-input': {
    title: '화장품 플랫폼 데이터관리 사무지원',
    team: '컨설팅본부',
    type: '정규직',
    mainTasks: [
      '• 고객사(브랜드 및 제조사) 응대',
      '• 화장품 서류 취합.검토 및 데이터관리',
    ],
    qualifications: ['• 경력 : 무관(신입 지원 가능)', '• 학력 : 초대졸 이상'],
    preferentials: [
      '• 기본적인 업무 툴 사용에 어려움이 없는 분',
      '• 적극적인 태도와 책임감을 갖춘 분',
      '• 일을 빠르고 정확하게 처리하시는 분',
      '• 정리 잘 하고 꼼꼼하신 분',
      '• 영문 서류 검토에 거부감이 없는 분',
      '• 데이터를 정확하고 꼼꼼하게 다루는 데에 보람을 느끼는 분',
      '• SaaS(웹 기반 업무 시스템) 환경에서 일하는 데 관심이 있으신 분',
    ],
    year: '무관',
  },
};

const commonTextMap = {
  intro: [
    '• Certicos(인허가) : 36+개국 화장품 해외 수출을 위한 쉽고 빠른 인증 자동화 시스템',
    '• Cosbridge(커뮤니티) : Session, 채용, 정보 교류를 통한 화장품 산업 시너지 창출 커뮤니티',
    '• Cosguide(컨설팅) : 데이터 기반 AI 맞춤형 컨설팅을 통한 애로 사항 해결 및 통합 지원',
    '• Cosmetest(솔루션) : 시험, 임상 등의 데이터와 자체 개발 AI로 효율적인 시험 컨설팅',
    '• CertiPT : LLM과 멀티에이전트 기술을 활용한 인허가 원천 서류 분류, 분석 솔루션',
    '• Cosmecarrer: 화장품 업종의 인재 채용 솔루션 (신규 사업)',
  ],
  mediaCoverage: [
    {
      title: '• CDRI, 인증 자동화 플랫폼 개발... CPNP 10일까지 단축',
      link: 'https://www.meconomynews.com/news/articleView.html?idxno=65663',
    },
    {
      title:
        "• CDRI, 화장품 인증 자동화 플랫폼 'CERTICOS' 론칭 6개월 누적 인허가 2,000건 돌파",
      link: 'https://www.cosinkorea.com/news/article.html?no=46368',
    },
    {
      title:
        '• 한번의 서류만으로 35개국 인증 완료...CDRI, ‘K-뷰티 데이터화’ 선도',
      link: 'http://www.cncnews.co.kr/news/article.html?no=6970',
    },
    {
      title: '• CDRI, 해외규격인증획득지원사업 수행기관 선정',
      link: 'https://www.beautynury.com/news/view/101310/cat/20',
    },
    {
      title:
        '• CDRI, K-Global@실리콘밸리 2024 참가. AI·빅데이터 기반 인허가·수출 성과 가속하는 화장품 전문 플랫폼 역량 소개!',
      link: 'https://kr.aving.net/news/articleView.html?idxno=1794303',
    },
  ],
  cdriWorkPrinciples: [
    '• 모든 일의 중심에 ‘사람’이 있음을 기억합니다.',
    ': 모든 일은 사람으로부터 되기 때문에 올바른 커뮤니케이션 없이는 될 수 있는 것은 하나도 없습니다.',
    '• 작은 일에도 프로다운 책임감을 가집니다.',
    ': 작은 일들이 모여서 큰일이 되고, 책임이 없다면 성공도 없습니다.',
    '• 시행착오를 통해 혁신을 추구하는 스타트업 정신을 계승합니다.',
    ': 사회가 필요로 하는 불편한 부분들을 찾아서 집요하게 풀어나고 수많은 시행착오의 과정들을 통해 결국 해결합니다.',
    '• 적극적으로 토론하고, 최선을 다해 따릅니다.',
    ': 최종 의사결정을 할 때까진 최선을 다해 싸우되, 결정을 하게되면 최선을 다해 따릅니다.',
    '• 과감하고 새로운 시도가 혁신의 시작입니다.',
    ': 해보기 전까지 우리는 결과를 알 수 없고, 실패가 아니라 두려움이 혁신을 망칩니다.',
    '• 실수와 실패에서 얻은 사실과 깨달음을 공유합니다.',
    ': 실수와 실패의 해결책을 찾아 보완하고 또 도전하고 실수하고 실패하는 게 우리 일입니다.',
  ],
  serviceDevelopmentIntroduction: [
    '• 서비스개발본부는 플랫폼1팀, 기획운영1팀으로 구성되어 있습니다.',
    '• 플랫폼1팀에는 프론트엔드 개발자, 백엔드 개발자, AI 개발자가 소속되어 CDRI의 시스템을 고도화하고 안정적으로 유지되도록 운영합니다.',
    '• 기획운영1팀에는 서비스기획자, UI/UX디자이너가 소속되어 사용자의 최상의 경험을 고민하며 CDRI의 서비스를 설계하고 디자인 합니다.',
    '• 서비스개발본부는 다양한 직무로 구성되어 있기 때문에 각 파트별 화합과 협업을 가장 중요하게 생각합니다.',
    '• 데일리스크럼을 통해 업무와 이슈를 공유하고, 서로 적극적인 피드백을 나누며 해결책을 함께 찾아 냅니다.',
    '• 외부 개발 어드바이저: 정규환 교수님',
    '- SK텔레콤 플랫폼기술원 매니저',
    '- SK플래닛 플랫폼기술원 매니저',
    '- 삼성전자 종합기술원 전문연구원',
    '- (주)뷰노 공동창업자 및 CTO 외 다수',
  ],
};

const serviceDevelopmentPositions = [
  'frontend-developer',
  'backend-developer',
  'ai-engineer',
  'e-commerce-planner',
];

const benefits = [
  '• 점심 및 저녁 식대 제공(점심 월 200,000원 / 저녁식대 12,000원)',
  '• 연말 휴가(4일) 제공',
  '• 기념일 연차 : 생일 연차 1일 제공(수습 통과 후)',
  '• 경조금과 경조휴가도 당연히 지원',
  '• 팜스킨 제품을 원가보다 낮은 금액으로 구매',
  '• 장기 근속 휴가 도입 (3년 : 10일 / 6년 : 15일)',
];

const lives = [
  {
    text: '• 유연 출퇴근제 운영 (8시~10시 출근 / 코어근무시간 10시~17시)',
  },
  {
    text: '• 2시간 단위로 자유로운 휴가 사용',
  },
  {
    text: '• 온오프 교육 & 헬스케어 등 연간 개인 성장지원금 60만원',
  },
  {
    text: '• 업무에 필요한 장비(맥북 프로, 듀얼 모니터 등) 및 소프트웨어를 지원',
    position: 'serviceDevelopments',
  },
  {
    text: '• 희망 도서 신청 시 회사에서 지원 & 자유로운 책 리뷰 공유',
  },
  {
    text: '• 영어 닉네임을 사용하여 수평적으로 소통',
  },
  {
    text: '• 분기별 타운홀 미팅을 통해 주요 이슈 사항과 경영 현황 공유',
  },
];

const URLS = {
  CDRI_HOMEPAGE: 'https://www.cdri.pro/',
  CDRI_COMPANY_INTRO: 'https://bit.ly/3BHv5od',
  APPLY_URL:
    'https://docs.google.com/forms/d/126wMU7xPcZ5sVuWtuV6kNnACLMsKn_3bdqHUNkAl7eY/edit',
};

const JobDetail2 = () => {
  const { positionKey } = useParams<{ positionKey: PositionKey }>();
  const { setIsScrolling } = useScroll();
  const isMobile = useMediaQuery({
    query: breakPoint.MOBILE,
  });
  const isTabletToLD = useMediaQuery({
    query: breakPoint.TABLET_TO_LARGE_DEVICE,
  });
  const isLD = useMediaQuery({
    query: breakPoint.LARGE_DEVICE,
  });

  const filterLives = useMemo(() => {
    if (!positionKey) return lives.map((life) => life.text);

    const isServiceDevelopment =
      serviceDevelopmentPositions.includes(positionKey);

    return lives
      .filter((item) =>
        item.position === 'serviceDevelopments' ? isServiceDevelopment : true,
      )
      .map((item) => item.text);
  }, [positionKey]);

  useLayoutEffect(() => {
    setIsScrolling(true);
  }, [setIsScrolling]);

  useEffect(() => {
    const mapScript = document.createElement('script');
    mapScript.async = true;
    mapScript.src = `//dapi.kakao.com/v2/maps/sdk.js?appkey=c4ae4174aa182f57eed619b7ca767248&autoload=false&libraries=services`;

    document.head.appendChild(mapScript);

    const onLoadKakaoMap = () => {
      try {
        if (window.kakao) {
          window.kakao.maps.load(() => {
            const boContainer = document.getElementById('boMap');
            const boPosition = new window.kakao.maps.LatLng(
              37.4973502733284,
              127.038096510953,
            );

            const boOptions = {
              // HINT : 지도의 기본 위도, 경도 값 (화원빌딩)
              center: boPosition,
            };
            const boMap = new window.kakao.maps.Map(boContainer, boOptions);

            new window.kakao.maps.Marker({
              map: boMap,
              position: boPosition,
            });
          });
        } else {
          throw new Error('Invalid KakaoMap');
        }
      } catch {
        console.error('카카오 맵을 불러올 수 없습니다.');
      }
    };
    mapScript.addEventListener('load', onLoadKakaoMap);

    return () => mapScript.removeEventListener('load', onLoadKakaoMap);
  }, []);

  if (!positionKey || !Object.keys(positionMap).includes(positionKey)) {
    return (
      <PageLayout scrollSnap={false}>
        <Container isLD={isLD}>잘못된 접근입니다</Container>
      </PageLayout>
    );
  }
  const {
    title,
    role,
    team,
    type,
    mainTasks,
    qualifications,
    technicalSkills,
    skills,
    preferentials,
    newPreferentials,
    year,
    positionIntroduction,
  } = positionMap[positionKey];

  return (
    <PageLayout scrollSnap={false}>
      <Container isLD={isLD}>
        <StyledFlex isLD={isLD} dir={!isLD ? 'row' : 'column'}>
          <LeftContainer>
            {!isLD && (
              <Header isMobile={isMobile} isTabletToLD={isTabletToLD}>
                <Typography.Title
                  type={isMobile ? 'h3' : isTabletToLD ? 'h2' : 'h1'}
                >
                  {title}
                </Typography.Title>
                <Typography.Title
                  css={css`
                    font-size: 24px;
                    font-weight: 400;
                    line-height: 34px;
                    margin-top: 16px;
                    color: #393e43;

                    ${isMobile &&
                    css`
                      margin-top: 8px;
                      font-size: 16px;
                      line-height: 28px;
                    `}
                    ${isTabletToLD &&
                    css`
                      margin-top: 8px;
                    `}
                  `}
                >
                  {team} - {type}
                </Typography.Title>
              </Header>
            )}
            <Content dir="column">
              <Subtitle
                isMobile={isMobile}
                type="h3"
                isTabletToLD={isTabletToLD}
              >
                🏢 CDRI 소개
              </Subtitle>
              <Typography.Title
                type="b2"
                color="DARK_GREY"
                css={css`
                  white-space: pre-line;

                  ${isMobile &&
                  css`
                    font-size: 14px;
                    line-height: 22x;
                  `};
                `}
              >
                [CDRI는 화장품 산업 내 노동 집약적이고 비효율적인 부분을
                해결하기 위해 IT기술과 서비스를 만들고 있습니다]
                <br />
                <br />
                {commonTextMap.intro.map((text) => (
                  <Description
                    isMobile={isMobile}
                    isTabletToLD={isTabletToLD}
                    type="b2"
                    key={text}
                    dangerouslySetInnerHTML={{ __html: text }}
                  />
                ))}
                <br />
                화장품 산업은 약 400조 정도로 크고 매년 5% 이상 성장하고 있는데,
                왜 우리는 지금도 대부분의 화장품 업무를 사람의 역량, 경험과
                노하우에만 의존하고 있을까요? 원료 개발, 제품 개발, 브랜드 개발,
                브랜드 및 제품 판매 등을 하면서 화장품 산업에 들어와보니, 뷰티
                산업 내 노동집약적이고 비효율적인 부분이 많았습니다. CDRI는 그
                중 Testing, Inspection, Certification 부분에 대한 비효율을
                IT기술과 서비스로 해결하고자 했습니다.
                <br />
                <br />
                CDRI는 자체 인증 시스템, ERP 구축을 통해 업무를 효율화 하고, AI
                스크리닝, LLM과 멀티에이전트 기술을 활용한 CertiPT를 통해 화장품
                산업 내 IT기술을 선도합니다. 플랫폼 오픈 이후 4년 만에 누적
                고객사 1,400개, 임상시험 3,700건 컨설팅, 특허 등록 10건, 매년
                평균 2.5배 이상의 매출 성장을 이어가며 업계에서 저력을 인정받고
                있습니다. 뷰티 IT 솔루션 프로바이더 CDRI에서 화장품 산업의 IT화
                선두를 함께 이끌어갈 동료를 찾습니다.
                <br />
                <br />
                [언론에 소개된 CDRI]
                <br />
                {commonTextMap.mediaCoverage.map((media) => (
                  <Description
                    isMobile={isMobile}
                    isTabletToLD={isTabletToLD}
                    type="b2"
                    key={media.title}
                  >
                    {media.title}
                    <div>
                      <span>: 기사 링크&nbsp;|&nbsp;</span>
                      <ExternalAnchor
                        href={media.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {media.link}
                      </ExternalAnchor>
                    </div>
                  </Description>
                ))}
                <br />
                [CDRI가 일하는 방식]
                <br />
                {commonTextMap.cdriWorkPrinciples.map((text) => (
                  <Description
                    isMobile={isMobile}
                    isTabletToLD={isTabletToLD}
                    type="b2"
                    key={text}
                    dangerouslySetInnerHTML={{ __html: text }}
                  />
                ))}
                <br />
                <div>
                  <span>[CDRI 홈페이지]&nbsp;:&nbsp;</span>
                  <ExternalAnchor
                    href={URLS.CDRI_HOMEPAGE}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {URLS.CDRI_HOMEPAGE}
                  </ExternalAnchor>
                </div>
                <div>
                  <span>[CDRI 회사 소개]&nbsp;:&nbsp;</span>
                  <ExternalAnchor
                    href={URLS.CDRI_COMPANY_INTRO}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {URLS.CDRI_COMPANY_INTRO}
                  </ExternalAnchor>
                </div>
                {serviceDevelopmentPositions.includes(positionKey) && (
                  <Fragment>
                    <br />
                    <br />
                    [서비스개발본부 조직 소개]
                    <br />
                    {commonTextMap.serviceDevelopmentIntroduction.map(
                      (text) => (
                        <Description
                          isMobile={isMobile}
                          isTabletToLD={isTabletToLD}
                          type="b2"
                          key={text}
                          dangerouslySetInnerHTML={{ __html: text }}
                        />
                      ),
                    )}
                  </Fragment>
                )}
              </Typography.Title>
              <Subtitle
                isMobile={isMobile}
                type="h3"
                isTabletToLD={isTabletToLD}
              >
                📑 주요업무
              </Subtitle>
              {positionKey === 'frontend-developer' && (
                <Fragment>
                  <Description
                    type="b2"
                    isMobile={isMobile}
                    isTabletToLD={isTabletToLD}
                  >
                    [AI와 함께 성장하는 개발자, 함께 만들어가요!]
                  </Description>
                  <Description
                    type="b2"
                    isMobile={isMobile}
                    isTabletToLD={isTabletToLD}
                  >
                    논리적 문제 해결 능력과 AI 도구를 활용한 혁신적인 개발
                    방식을 통해, 최고의 사용자 경험을 만들어가는 프론트엔드
                    개발자를 찾고 있습니다.
                  </Description>
                  <br />
                </Fragment>
              )}

              {positionIntroduction && (
                <Fragment>
                  <Description
                    type="b2"
                    isMobile={isMobile}
                    isTabletToLD={isTabletToLD}
                  >
                    [포지션 소개]
                  </Description>
                  {positionIntroduction.map((intro) => (
                    <Description
                      type="b2"
                      key={intro}
                      isMobile={isMobile}
                      isTabletToLD={isTabletToLD}
                    >
                      {intro}
                    </Description>
                  ))}
                  <br />
                  <Description
                    type="b2"
                    isMobile={isMobile}
                    isTabletToLD={isTabletToLD}
                  >
                    [주요 업무]
                  </Description>
                </Fragment>
              )}

              {mainTasks.map((task) => (
                <Description
                  isMobile={isMobile}
                  isTabletToLD={isTabletToLD}
                  type="b2"
                  key={task}
                  dangerouslySetInnerHTML={{ __html: task }}
                />
              ))}
              <Subtitle
                type="h3"
                isMobile={isMobile}
                isTabletToLD={isTabletToLD}
              >
                💡 자격요건
              </Subtitle>
              {positionKey === 'frontend-developer' && (
                <Description
                  type="b2"
                  isMobile={isMobile}
                  isTabletToLD={isTabletToLD}
                >
                  [핵심 역량]
                </Description>
              )}
              {qualifications.map((qualification) => (
                <Description
                  type="b2"
                  key={qualification}
                  isMobile={isMobile}
                  isTabletToLD={isTabletToLD}
                >
                  {qualification}
                </Description>
              ))}
              {technicalSkills && (
                <Fragment>
                  <br />
                  <Description
                    type="b2"
                    isMobile={isMobile}
                    isTabletToLD={isTabletToLD}
                  >
                    [기술 역량]
                  </Description>
                  {technicalSkills.map((qualification) => (
                    <Description
                      type="b2"
                      key={qualification}
                      isMobile={isMobile}
                      isTabletToLD={isTabletToLD}
                    >
                      {qualification}
                    </Description>
                  ))}
                </Fragment>
              )}

              {skills && (
                <Fragment>
                  <SkillStack
                    type="b2"
                    isMobile={isMobile}
                    isTabletToLD={isTabletToLD}
                  >
                    <b>기술 스택/툴</b>
                  </SkillStack>
                  {skills.map((skill) => (
                    <Description
                      type="b2"
                      key={skill}
                      isMobile={isMobile}
                      isTabletToLD={isTabletToLD}
                    >
                      {skill}
                    </Description>
                  ))}
                </Fragment>
              )}
              {(preferentials || newPreferentials) && (
                <Fragment>
                  <Subtitle
                    isMobile={isMobile}
                    type="h3"
                    isTabletToLD={isTabletToLD}
                  >
                    👍 우대사항
                  </Subtitle>

                  {preferentials &&
                    preferentials.map((preferential) => (
                      <Description
                        type="b2"
                        key={preferential}
                        isMobile={isMobile}
                        isTabletToLD={isTabletToLD}
                      >
                        {preferential}
                      </Description>
                    ))}

                  {newPreferentials && (
                    <Fragment>
                      {newPreferentials.map((item, idx) => (
                        <Fragment key={`newPreferentials-${idx}`}>
                          {idx > 0 && <br />}
                          <Description
                            type="b2"
                            isMobile={isMobile}
                            isTabletToLD={isTabletToLD}
                          >
                            {item.title}
                          </Description>
                          {item.contents.map((content) => (
                            <Description
                              type="b2"
                              key={content}
                              isMobile={isMobile}
                              isTabletToLD={isTabletToLD}
                            >
                              {content}
                            </Description>
                          ))}
                        </Fragment>
                      ))}
                    </Fragment>
                  )}
                  {positionKey === 'data-input' && (
                    <Fragment>
                      <br />
                      <Description
                        type="b2"
                        isMobile={isMobile}
                        isTabletToLD={isTabletToLD}
                      >
                        <b>SaaS : 웹 기반 업무 시스템(Software as a Service)</b>
                      </Description>
                      <Description
                        type="b2"
                        isMobile={isMobile}
                        isTabletToLD={isTabletToLD}
                      >
                        SaaS는 회사에서 사용하는 전용 웹 시스템으로, 화장품 관련
                        데이터를 효율적으로 관리하고 자동화하는 플랫폼입니다.
                      </Description>
                      <Description
                        type="b2"
                        isMobile={isMobile}
                        isTabletToLD={isTabletToLD}
                      >
                        CDRI는 자체 개발한 SaaS 플랫폼을 통해 화장품 관련
                        데이터를 웹에서 입력하고 관리합니다. 인터넷만으로 접속
                        가능한 회사 전용 시스템으로, 누구나 쉽게 배워 사용할 수
                        있습니다.
                      </Description>
                    </Fragment>
                  )}
                </Fragment>
              )}

              <Subtitle
                isMobile={isMobile}
                type="h3"
                isTabletToLD={isTabletToLD}
              >
                🌟 혜택 및 복지
              </Subtitle>
              <Description
                type="b2"
                isMobile={isMobile}
                isTabletToLD={isTabletToLD}
              >
                <b>BENEFITS</b>
              </Description>
              {benefits.map((benefit, idx) => (
                <Description
                  key={`benefit-${idx}`}
                  type="b2"
                  isMobile={isMobile}
                  isTabletToLD={isTabletToLD}
                  style={{ marginTop: idx === 0 ? 8 : 0 }}
                >
                  {benefit}
                </Description>
              ))}

              <Description
                type="b2"
                css={css`
                  margin-top: 24px;

                  ${isMobile &&
                  css`
                    margin-top: 32px;
                  `}
                  ${isTabletToLD &&
                  css`
                    margin-top: 48px;
                  `}
                `}
                isMobile={isMobile}
                isTabletToLD={isTabletToLD}
              >
                <b>LIFE</b>
              </Description>
              {filterLives.map((text, idx) => (
                <Description
                  key={`life-${idx}`}
                  type="b2"
                  isMobile={isMobile}
                  isTabletToLD={isTabletToLD}
                  style={{ marginTop: idx === 0 ? 8 : 0 }}
                >
                  {text}
                </Description>
              ))}
              <Subtitle
                isMobile={isMobile}
                type="h3"
                isTabletToLD={isTabletToLD}
              >
                🎉 채용 전형
              </Subtitle>
              <Description
                type="b2"
                isMobile={isMobile}
                isTabletToLD={isTabletToLD}
              >
                <Typography.Text
                  weight={700}
                  type={isMobile ? 'b3' : 's2'}
                  color="DARK_GREY"
                  css={css`
                    word-break: keep-all;
                  `}
                >
                  입사지원 &gt; 서류 전형
                  {title.includes('개발자') &&
                    '(1차 인터뷰 전 과제 테스트 있음)'}{' '}
                  &gt; 1차(실무자) 인터뷰 &gt; 최종(경영진) 인터뷰 &gt; 최종
                  오퍼 확정 후 오퍼 레터 발송
                </Typography.Text>
              </Description>
              <Subtitle
                isMobile={isMobile}
                type="h3"
                isTabletToLD={isTabletToLD}
              >
                ✔️ 참고 사항
              </Subtitle>
              <Description
                type="b2"
                isMobile={isMobile}
                isTabletToLD={isTabletToLD}
              >
                • 3개월의 수습기간이 있습니다.
              </Description>
              <Description
                type="b2"
                isMobile={isMobile}
                isTabletToLD={isTabletToLD}
              >
                • 입사 지원 서류에서 허위기재 사실이 발견될 경우, 입사가 취소될
                수 있습니다.
              </Description>
              <Description
                type="b2"
                isMobile={isMobile}
                isTabletToLD={isTabletToLD}
              >
                • 직무 또는 상황에 따라 전형 절차는 변경될 수 있습니다.
              </Description>
              <Description
                type="b2"
                isMobile={isMobile}
                isTabletToLD={isTabletToLD}
              >
                • 국가유공자 및 장애인 등 취업보호대상자는 관계 법령에 의거하여
                우대합니다.
              </Description>
            </Content>
          </LeftContainer>
          <RightContainer dir="column">
            {isLD && (
              <Header isMobile={isMobile} isTabletToLD={isTabletToLD}>
                <Typography.Title
                  type={isMobile ? 'h3' : isTabletToLD ? 'h2' : 'h1'}
                >
                  {title}
                </Typography.Title>
                <Typography.Title
                  css={css`
                    font-size: 24px;
                    font-weight: 400;
                    line-height: 34px;
                    margin-top: 16px;
                    color: #393e43;

                    ${isMobile &&
                    css`
                      margin-top: 8px;
                      font-size: 16px;
                      line-height: 28px;
                    `}
                    ${isTabletToLD &&
                    css`
                      margin-top: 8px;
                    `}
                  `}
                >
                  {team} - {type}
                </Typography.Title>
              </Header>
            )}
            <SummaryContainer>
              <Flex
                gap={32}
                style={{
                  borderBottom: `1px solid #E0E2E8`,
                  paddingBottom: '16px',
                }}
              >
                <Typography.Text
                  type="b3"
                  color="BLUE_GREY"
                  style={{ width: 52 }}
                >
                  직군
                </Typography.Text>
                <Typography.Text type="b3" color="BLACK">
                  {role || title}
                </Typography.Text>
              </Flex>
              <Flex
                gap={32}
                style={{
                  borderBottom: `1px solid #E0E2E8`,
                  padding: '16px 0',
                }}
              >
                <Typography.Text
                  type="b3"
                  color="BLUE_GREY"
                  style={{ width: 52 }}
                >
                  경력사항
                </Typography.Text>
                <Typography.Text type="b3" color="BLACK">
                  {year}
                </Typography.Text>
              </Flex>
              <Flex
                gap={32}
                style={{
                  borderBottom: `1px solid #E0E2E8`,
                  padding: '16px 0',
                }}
              >
                <Typography.Text
                  type="b3"
                  color="BLUE_GREY"
                  style={{ width: 52 }}
                >
                  고용형태
                </Typography.Text>
                <Typography.Text type="b3" color="BLACK">
                  정규직
                </Typography.Text>
              </Flex>
              <Flex
                gap={32}
                style={{
                  padding: '16px 0',
                }}
              >
                <Typography.Text
                  type="b3"
                  color="BLUE_GREY"
                  style={{ width: 52 }}
                >
                  근무지
                </Typography.Text>
                <Typography.Text type="b3" color="BLACK">
                  강남구 논현로 417 화원빌딩 가든층 A
                </Typography.Text>
              </Flex>
              <Map id="boMap" />
            </SummaryContainer>
            {!isLD && (
              <SubmitButton
                onClick={() => window.open(URLS.APPLY_URL, '_blank')}
              >
                지원하기
              </SubmitButton>
            )}
          </RightContainer>
        </StyledFlex>
        <HashLink to="/job#positions" smooth>
          <Flex
            css={css`
              margin-top: 80px;
              margin-bottom: 120px;
              cursor: pointer;

              ${isMobile &&
              css`
                margin: 24px 0;
              `}
            `}
            gap={isMobile ? 8 : 14}
            align="center"
          >
            <ArrowLeft
              css={css`
                ${isMobile &&
                css`
                  width: 24px;
                  height: 24px;
                `}
                ${isTabletToLD &&
                css`
                  width: 40px;
                  height: 40px;
                `}
              `}
            />
            <Typography.Text
              type="b1"
              css={css`
                ${isMobile &&
                css`
                  font-size: 16px;
                  line-height: 24px;
                `}
                ${isTabletToLD &&
                css`
                  font-size: 18px;
                  line-height: 28px;
                `}
              `}
            >
              목록으로 돌아가기
            </Typography.Text>
          </Flex>
        </HashLink>
        {isLD && (
          <ButtonContainer>
            <SubmitButton onClick={() => window.open(URLS.APPLY_URL, '_blank')}>
              지원하기
            </SubmitButton>
          </ButtonContainer>
        )}
      </Container>
    </PageLayout>
  );
};

export default JobDetail2;

const Container = styled.section<{ isLD: boolean }>`
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  padding-top: 200px;
  padding-bottom: 200px;

  ${({ isLD }) =>
    isLD &&
    css`
      padding: 0 24px !important;
    `}
`;

const LeftContainer = styled(Flex)`
  flex-direction: column;
`;

const RightContainer = styled(Flex)`
  flex-direction: column;
  width: 368px;
  gap: 32px;
  @media ${breakPoint.LARGE_DEVICE} {
    width: 100%;
    margin-top: 116px;
  }
`;

const SummaryContainer = styled(Flex)`
  flex-direction: column;
  border-radius: 8px;
  width: 100%;
  border: 1px solid ${palette.LIGHT_GREY2};
  padding: 16px 24px;

  @media ${breakPoint.LARGE_DEVICE} {
    padding: 24px;
  }
`;

const Header = styled.div<{ isMobile: boolean; isTabletToLD: boolean }>`
  /* ${({ isMobile }) =>
    isMobile &&
    css`
      padding-bottom: 24px;
      margin-top: 116px;
    `}

  ${({ isTabletToLD }) =>
    isTabletToLD &&
    css`
      padding-bottom: 32px;
      margin-top: 180px;
    `} */
`;

const StyledFlex = styled(Flex)<{ isLD: boolean }>`
  justify-content: space-between;
  gap: 80px;

  ${({ isLD }) =>
    isLD &&
    css`
      flex-direction: column-reverse;
      justify-content: start;
      gap: 0;
    `}
`;

const Content = styled(Flex)`
  flex-direction: column;
`;

const Subtitle = styled(Typography.Title)<{
  isMobile: boolean;
  isTabletToLD: boolean;
}>`
  margin: 56px 0px 16px;

  ${({ isMobile }) =>
    isMobile &&
    css`
      font-size: 18px;
      line-height: 28px;
      margin: 32px 0px 8px;
    `}

  ${({ isTabletToLD }) =>
    isTabletToLD &&
    css`
      font-size: 24px;
      line-height: 34px;
      margin: 48px 0px 16px;
    `}
`;

const Description = styled(Typography.Title)<{
  isMobile: boolean;
  isTabletToLD: boolean;
}>`
  b {
    font-weight: 700;
  }

  ${({ isMobile }) =>
    isMobile &&
    css`
      font-size: 14px;
      line-height: 22px;
    `}

  ${({ isTabletToLD }) =>
    isTabletToLD &&
    css`
      font-size: 16px;
      line-height: 24px;
    `}
`;

const SkillStack = styled(Description)`
  margin: 32px 0px 16px;

  ${({ isMobile }) =>
    isMobile &&
    css`
      margin: 32px 0px 8px;
    `}

  ${({ isTabletToLD }) =>
    isTabletToLD &&
    css`
      margin: 48px 0px 16px;
    `}
`;

const SubDescription = styled(Typography.Title)`
  padding-left: 16px;
  color: ${palette.DARK_GREY};
`;

const ButtonContainer = styled.div`
  background-color: white;
  position: sticky;
  bottom: 0;
  width: 100%;
  padding: 24px 0;
  z-index: 100;
`;

const SubmitButton = styled(Flex)`
  background-color: ${palette.PRIMARY};
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  width: 368px;
  height: 44px;
  color: white;
  cursor: pointer;
  transition: all 200ms ease-in;
  @media ${breakPoint.LARGE_DEVICE} {
    width: 100%;
  }
  &:hover {
    background-color: #c21017;
  }
`;

const Map = styled.div`
  flex: 1 1 auto;
  height: 160px;
`;

const ExternalAnchor = styled.a`
  word-break: break-all;
  overflow-wrap: break-word;
  &:hover {
    text-decoration: underline;
  }
`;
